import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConfigType } from 'src/data/datatypes';
import * as dbService from '../services/DBService/DbService';
import { RootState } from './store';
import { Userdata } from '@app/common';

export interface GlobalState {
    user?: Userdata;
    connectionState: dbService.ConnectionState;
    syncProgress?: number;
    config?: ConfigType | null;
    dbFatalErrorCode?: string;
    dbFatalErrorHttpStatus?: number;
}

const initialState: GlobalState = {
    connectionState: dbService.ConnectionState.DISCONNECTED
};

export const connectToDB = createAsyncThunk<
    { user: Userdata, config: ConfigType | null },
    { username: string, password: string },
    { state: RootState, rejectValue: dbService.ConnectionState.CONNECTION_FAILED }
>(
    'globalState/connectDB',
    async ({ username, password }, thunkAPI) => {
        const connectIter = dbService.connect(username, password);
        while (true) {
            const res = await connectIter.next();
            const { value, done } = res;
            if (!done) {
                if (typeof value === 'number') {
                    thunkAPI.dispatch(globalState.actions.setConnectionState(value));
                }
            } else {
                if (value === dbService.ConnectionState.CONNECTION_FAILED) {
                    return thunkAPI.rejectWithValue(value);
                } else if (typeof value === 'object') {
                    return value;
                } else {
                    throw new Error('Unexpected connection error');
                }
            }
        }
    }
);

export const babyBorn = createAsyncThunk<
    Userdata,
    void,
    { state: RootState }
>(
    'globalState/babyBorn',
    async (_, thunkApi) => {
        try {
            const user = thunkApi.getState().globalState.user;
            if (user) {
                const newUser: Userdata = {
                    ...user,
                    stateProperties: [
                        ...user.stateProperties.filter((p: any) => (p !== 'PREGNANCY' && p !== 'POSTPARTUM')),
                        'POSTPARTUM'
                    ]
                };
                await dbService.saveUser(newUser);
                return newUser;
            } else {
                return thunkApi.rejectWithValue(new Error('Cannot set BabyBorn: No user object in state'));
            }
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

export const setDoTutorial = createAsyncThunk<
    Userdata,
    boolean, // false: DO_TUTORIAL is removed from userproperties, true: DO_TUTORIAL is added
    { state: RootState }
>(
    'globalState/setDoTutorial',
    async (addDoTutorial, thunkApi) => {
        try {
            const user = thunkApi.getState().globalState.user;
            if (user) {
                const newUser: Userdata = {
                    ...user,
                    userProperties: [
                        ...user.userProperties.filter(property => property !== 'DO_TUTORIAL')   // remove DO_TUTORIAL at first
                    ]
                };
                if (addDoTutorial) {
                    newUser.userProperties.push('DO_TUTORIAL');
                }
                await dbService.saveUser(newUser);
                return newUser;
            } else {
                return thunkApi.rejectWithValue(new Error('Cannot set doTutorial: No user object in state'));
            }
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

export const setCoachId = createAsyncThunk<
    Userdata,
    `coach_${string}`,
    { state: RootState, rejectValue: Error }
>(
    'globalState/setCoachId',
    async (coachId, thunkApi) => {
        try {
            const user = thunkApi.getState().globalState.user;
            if (user) {
                const newUser: Userdata = {
                    ...user,
                    coachId
                };
                await dbService.saveUser(newUser);
                return newUser;
            } else {
                return thunkApi.rejectWithValue(new Error('Cannot set CoachId: No user object in state'));
            }
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

export const setUserVariables = createAsyncThunk<
    Userdata,
    Userdata['variables'],
    { state: RootState, rejectValue: Error }
>(
    'globalState/setUserVariables',
    async (variables, thunkApi) => {
        try {
            const user = thunkApi.getState().globalState.user;
            if (user) {
                const newUser = {
                    ...user,
                    variables: {
                        ...user.variables,
                        ...variables
                    }
                };
                await dbService.saveUser(newUser);
                return newUser;
            } else {
                return thunkApi.rejectWithValue(new Error('Cannot set UserVariables: No user object in state'));
            }
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

// used by tenants who want to set start date to today on initial login (config.setUserStartDateOnInitialLogin === true)
export const setUserStartDate = createAsyncThunk<
    Userdata,
    Userdata['startDate'],
    { state: RootState, rejectValue: Error }
>(
    'globalState/setUserStartDate',
    async (startDate, thunkApi) => {
        try {
            const user = thunkApi.getState().globalState.user;
            if (user) {
                const newUser = {
                    ...user,
                    startDate
                };
                await dbService.saveUser(newUser);
                return newUser;
            } else {
                return thunkApi.rejectWithValue(new Error('Cannot set user start date: No user object in state'));
            }
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

export const _devSetUser = createAsyncThunk<
    Userdata | undefined,
    Userdata | undefined,
    { state: RootState }
>(
    'globalState/_devSetUser',
    async (user, thunkApi) => {
        try {
            if (user !== undefined) {
                await dbService.saveUser(user);
            }
            return user;
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

export const _devResetUser = createAsyncThunk<Userdata, Userdata, { state: RootState }>(
    'globalState/_devResetUser',
    async (user, thunkApi) => {
        try {
            await dbService.saveUser(user);
            return user;
        } catch (e) {
            return thunkApi.rejectWithValue(e as Error);
        }
    }
);

const globalState = createSlice({
    name: 'globalState',
    initialState,
    reducers: {   
        setConnectionState: (state, action: PayloadAction<dbService.ConnectionState>) => {
            state.connectionState = action.payload;
        },
        setSyncProgress: (state, action: PayloadAction<number>) => {
            state.syncProgress = action.payload;
        },
        applyUserdataChangeFromDb: (state, action: PayloadAction<Userdata>) => {
            state.user = action.payload;
        },
        applyFatalDbError: (state, action: PayloadAction<{ code: string, httpStatus?: number }>) => {
            state.user = undefined;
            state.config = undefined;
            state.dbFatalErrorCode = action.payload.code;
            state.dbFatalErrorHttpStatus = action.payload.httpStatus;
            state.connectionState = dbService.ConnectionState.CONNECTION_FAILED;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(connectToDB.fulfilled, (state, { payload }) => {
            state.user = payload.user;
            state.config = payload.config;
            state.dbFatalErrorCode = undefined;
            state.dbFatalErrorHttpStatus = undefined;
        });
        builder.addCase(connectToDB.rejected, (state, { payload }) => {
            state.connectionState = dbService.ConnectionState.CONNECTION_FAILED;
        });

        builder.addCase(setCoachId.fulfilled, (state, { payload }) => {
            state.user = payload;
        });

        builder.addCase(babyBorn.fulfilled, (state, { payload }) => {
            state.user = payload;
        });

        builder.addCase(setDoTutorial.fulfilled, (state, { payload }) => {
            state.user = payload;
        });

        builder.addCase(setUserVariables.fulfilled, (state, { payload }) => {
            state.user = payload;
        });

        builder.addCase(_devSetUser.fulfilled, (state, { payload }) => {
            state.user = payload;
        });

        builder.addCase(_devResetUser.fulfilled, (state, { payload }) => {
            state.user = payload;
        });
        builder.addCase(setUserStartDate.fulfilled, (state, { payload }) => {
            state.user = payload;
        });
    }
});

export const { setConnectionState, setSyncProgress, applyUserdataChangeFromDb, applyFatalDbError } = globalState.actions;

export default globalState;
